/// <reference types="google.maps" />

let loadPromise: Promise<typeof google.maps> | null = null;

/**
 * Lazy-load the Google Maps JS API. Returns the cached `google.maps` namespace
 * after the first call. Throws if the API key is missing or the script fails.
 *
 * `language` is locked at script-load time — Google Maps JS can't switch
 * label language without a fresh page load. The first call wins; subsequent
 * calls with a different language are ignored (they just hit the cache).
 */
export function loadGoogleMaps(language?: string): Promise<typeof google.maps> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('loadGoogleMaps called outside the browser'));
      return;
    }
    if (window.google?.maps) {
      resolve(window.google.maps);
      return;
    }
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_JS_API_KEY as string | undefined;
    if (!apiKey) {
      reject(new Error('VITE_GOOGLE_MAPS_JS_API_KEY is not set'));
      return;
    }

    const cb = `__lt_maps_init_${Math.random().toString(36).slice(2)}`;
    const winRecord = window as unknown as Record<string, () => void>;
    const cleanupCb = () => {
      delete winRecord[cb];
    };
    winRecord[cb] = () => {
      cleanupCb();
      resolve(window.google.maps);
    };

    let src =
      `https://maps.googleapis.com/maps/api/js` +
      `?key=${encodeURIComponent(apiKey)}` +
      `&loading=async` +
      `&v=weekly` +
      `&callback=${cb}`;
    if (language) src += `&language=${encodeURIComponent(language)}`;

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onerror = () => {
      cleanupCb();
      loadPromise = null;
      reject(new Error('Failed to load Google Maps JS API'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}
